import { FunctionComponent, useState, Suspense, lazy, useRef, useReducer, useEffect } from 'react'
import { BackendUser, CommentPost, BackendPartner, CREATE_COMMENT_PLACEHOLDER } from 'common/types'
import styled from 'styled-components/macro'
import { useScreenSizes } from 'hooks/use-screen-sizes/useScreenSizes'
import { Box, Avatar, Typography } from '@material-ui/core'
import SendIcon from '@material-ui/icons/Send'
import { useAnalytics } from 'hooks/use-analytics/useAnalytics'
import { Loader } from 'components/core/loader'
import { MentionsTextfield } from '../posts/create-post/MentionsTextField'
import { MentionsSuggestionsList } from '../posts/create-post/MentionsSuggestionsList'
import { useMentions } from '../posts/hooks/useMentions'
import { IMentionsState } from 'common/types/mentions.types'
import { createMentionsState } from 'common/utils/createMentionsState'
import { getMentionsTags } from 'common/utils/getMentionsTags'
import { DEV_ONLY } from 'common/globalConstants'
import { getHTMLTextLength } from 'common/utils/getHTMLTextLength'
import { useCreateComment } from './hooks/useCreateComment'

const LazyConfirmProfileModal = lazy(() =>
  import('../posts/confirm-profile/ConfirmProfileModal').then(module => ({ default: module.ConfirmProfileModal }))
)

const StyledSendIcon = styled(SendIcon)`
  && {
    height: 1em;
    width: 1em;
  }
  &:hover {
    cursor: pointer;
  }
`

interface CreateCommentProps {
  user: BackendUser
  createComment: Function
  postId: String
  comments: CommentPost[]
  setComments: Function
  commentCount: number
  setCommentCount: Function
  handleComment: Function
  partner: BackendPartner
  partnerView?: boolean
  createCommentPermission: any
  theme: any
}
export const CreateComment: FunctionComponent<CreateCommentProps> = ({
  user,
  postId,
  comments,
  setComments,
  commentCount,
  setCommentCount,
  handleComment,
  partner,
  partnerView,
  createCommentPermission,
  theme
}) => {
  const { isDevice } = useScreenSizes()
  const [showConfirmProfile, setShowConfirmProfile] = useState(false)

  const {
    getResults,
    users,
    setUsers,
    setMentions,
    mentions,
    totalSuggestions,
    tags,
    setTags,
    setTagResults,
    tagResults,
    searchText,
    handlePostComment,
    commentMentionsState,
    commentMentionStateDispatch,
    maxCommentLength,
    commentResetMentioningRef,
    canPost
  } = useCreateComment(
    user,
    partner,
    partnerView,
    handleComment,
    postId,
    comments,
    setComments,
    setCommentCount,
    commentCount
  )

  return (
    <>
      <Box display="flex" flexDirection="row" width="100%">
        <Box width={isDevice ? '15%' : '11%'} display="flex" justifyContent="center">
          {!createCommentPermission && (
            <Avatar src={user?.image} alt="member with a profile" style={{ width: '2em', height: '2em' }} />
          )}
        </Box>
        <Box
          display="flex"
          alignItems="center"
          padding={isDevice ? '0' : '0 0.5em'}
          width={isDevice ? '95%' : '81%'}
          marginLeft={isDevice ? '0.5em' : 0}
        >
          <Box
            className="MuiInputBase-root MuiInput-root MuiInputBase-fullWidth MuiInput-fullWidth MuiInputBase-formControl MuiInput-formControl MuiInputBase-adornedEnd"
            padding="14px"
            display="flex"
            justifyContent="space-between"
          >
            <MentionsTextfield
              getResults={getResults}
              maxLength={maxCommentLength}
              mentionedContainerClass="comment-val-mentioned"
              setMentions={setMentions}
              setUsers={setUsers}
              typingID={commentMentionsState?.textfieldId}
              setCursorEl={(active: boolean) => {}}
              users={users}
              edit={false}
              theme={theme}
              setText={(val: string) => commentMentionStateDispatch({ textfieldValue: val })}
              setCharacterCount={characterLength => {
                commentMentionStateDispatch({ textfieldCharacterLength: characterLength })
              }}
              totalSuggestions={totalSuggestions}
              mentionedCounter={commentMentionsState?.mentionedCounter}
              setMentionedCounter={newCounterValue =>
                commentMentionStateDispatch({ mentionedCounter: newCounterValue })
              }
              resetMentioningRef={commentResetMentioningRef}
              isMentioning={commentMentionsState?.isMentioning}
              setIsMentioning={(val: boolean) => commentMentionStateDispatch({ isMentioning: val })}
              placeholder={CREATE_COMMENT_PLACEHOLDER}
              defaultValue={commentMentionsState?.textfieldValue}
              fullWidth
              width="100%"
              isTagging={commentMentionsState?.isTagging}
              setIsTagging={isTagging => commentMentionStateDispatch({ isTagging })}
              setTagResult={setTagResults}
              hasTagActive={DEV_ONLY}
              setHashCount={hashCount => commentMentionStateDispatch({ hashCount })}
              hashCount={commentMentionsState?.hashCount}
              tags={tags}
              mentions={mentions}
              setAutoTag={autoTag => commentMentionStateDispatch({ autoTag })}
              setSelectedTarget={selectedTarget => commentMentionStateDispatch({ selectedTarget })}
              selectedTarget={commentMentionsState?.selectedTarget}
              state={commentMentionsState}
              stateDispatch={commentMentionStateDispatch}
            />
          </Box>
          <Box paddingLeft={isDevice ? '0.2em' : '1em'}>
            <StyledSendIcon
              color={canPost ? 'primary' : 'disabled'}
              onClick={async () => {
                if (canPost) {
                  if (user?.partnerId && createCommentPermission) {
                    setShowConfirmProfile(true)
                    return
                  }

                  await handlePostComment(commentMentionsState?.textfieldValue)
                }
              }}
            />
          </Box>
        </Box>
      </Box>
      {commentMentionsState?.textfieldCharacterLength > 0 && (
        <>
          <Typography component="div" variant="caption" align="right">
            <Box
              mr={isDevice ? '10%' : '16%'}
              color={
                commentMentionsState?.textfieldCharacterLength >= maxCommentLength
                  ? theme.colors.error
                  : theme.colors.accent
              }
            >
              {commentMentionsState?.textfieldCharacterLength}/{maxCommentLength}
            </Box>
          </Typography>
        </>
      )}
      {showConfirmProfile && (
        <Box display="block" width="100%">
          <Suspense fallback={<Loader />}>
            <LazyConfirmProfileModal
              show={showConfirmProfile}
              setShow={setShowConfirmProfile}
              handlePostComment={handlePostComment}
              comment={commentMentionsState?.textfieldValue}
            />
          </Suspense>
        </Box>
      )}
      <Box width="73%" marginLeft="12%">
        <MentionsSuggestionsList
          value={commentMentionsState?.textfieldValue}
          getResults={getResults}
          mentionedContainerClass="comment-val-mentioned"
          mentionedCounter={commentMentionsState?.mentionedCounter}
          resetMentioningRef={commentResetMentioningRef}
          setMentions={setMentions}
          setText={(val: string) => commentMentionStateDispatch({ textfieldValue: val })}
          setCharacterCount={characterLength => {
            commentMentionStateDispatch({ textfieldCharacterLength: characterLength })
          }}
          totalSuggestions={totalSuggestions}
          typingID={commentMentionsState?.textfieldId}
          users={users}
          isMentioning={commentMentionsState?.isMentioning}
          setTagResults={setTagResults}
          isTagging={commentMentionsState?.isTagging}
          tagResults={tagResults}
          searchText={searchText}
          setTags={setTags}
          hasTagActive={false}
          hashCount={commentMentionsState?.hashCount}
          setAutoTag={autoTag => commentMentionStateDispatch({ autoTag })}
          autoTag={commentMentionsState.autoTag}
          setSelectedTarget={selectedTarget => commentMentionStateDispatch({ selectedTarget })}
        />
      </Box>
    </>
  )
}
